import React, { useContext, useState } from 'react';

import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import LogViewer from '../../components/logViewer';
import { AppContext } from '../../contexts/app-context';
import GlobalStyles from '../../global-styles';
import { getESPJobLogDetail } from '../../utils/helper';
import { ErrorIcon, RunIcon, ThumbUpIcon } from '../../utils/icons';
import {
  Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid,
  Typography
} from '../../utils/material-ui-core';

interface ConfirmProps {
  espJobLog: ESPJobLog;
  espJobKey: string;
  open: boolean;
  callRunButton: (referenceId: number) => void;
  callbackOnClose: () => void;
}

const BatchLogFiles = ({ espJobLog, open, espJobKey, callRunButton, callbackOnClose }: ConfirmProps) => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const { showAlert } = useContext(AppContext);

  const [showLogDetail, setShowLogDetail] = useState(false);
  const [logDetail, setLogDetail] = useState("");
  const [logDetailLoading, setLogDetailLoading] = useState(false);

  const handleLogDetailCloseClick = () => {
    setShowLogDetail(false);
  };

  const openLogDetail = async (fileKey: string, index: number) => {
    setLogDetail("");
    setLogDetailLoading(true);
    setShowLogDetail(true);

    const apiResponse = await getESPJobLogDetail(espJobKey, fileKey);

    if (apiResponse.success) {
      if (apiResponse.data.content !== null) {
        if (apiResponse.data.content.indexOf("Log Report") >= 0) {
          const wnd = window.open("about:blank", "LogData");
          if (wnd !== null) {
            wnd.document.write(apiResponse.data.content);
            wnd.document.close();
          }
          setLogDetailLoading(false);
          setShowLogDetail(false);
        } else {
          setLogDetail(apiResponse.data.content);
          setLogDetailLoading(false);
          return;
        }
      }
    } else {
      showAlert({ message: apiResponse.errorMessage, severity: "error" });
      setLogDetailLoading(false);
      setShowLogDetail(false);
    }
  };

  return (
    <>
      <LogViewer content={logDetail} okText="close" open={showLogDetail} loading={logDetailLoading} callbackOnClose={handleLogDetailCloseClick} />

      <Dialog className={globalStyles.dialogContainer} open={open} maxWidth={"sm"} fullWidth={true}>
        <DialogTitle id="input-dialog-title" className={globalStyles.dialogTitleContainer}>
          <Typography variant="subtitle1" component="div">
            Log Files
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText className={pageStyles.contentText} component="div">
            <Grid container spacing={3}>
              {espJobLog.espjobSubLogs.map((espjobSubLog, index) => (
                <Grid item key={index}>
                  <Chip label={espjobSubLog.jobId} variant="outlined" onClick={() => openLogDetail(espjobSubLog.fileKey, index)} icon={espjobSubLog.status.toLowerCase() === "running" ? <RunIcon fontSize="small" /> : espjobSubLog.status.toLowerCase() === "failed" ? <ErrorIcon fontSize="small" /> : <ThumbUpIcon fontSize="small" />} className={espjobSubLog.status.toLowerCase() === "running" ? pageStyles.running : espjobSubLog.status.toLowerCase() === "failed" ? pageStyles.failed : pageStyles.completed} />
                </Grid>
              ))}
            </Grid>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          {espJobLog.showRunButton ? (
            <Button onClick={() => callRunButton(espJobLog.jobId)} color="secondary" variant="contained" autoFocus className={globalStyles.buttonDefault}>
              Run
            </Button>
          ) : null}
          <Button onClick={() => callbackOnClose()} color="secondary" variant="contained" autoFocus className={globalStyles.buttonDefault}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    contentText: {
      whiteSpace: "pre-wrap",
      "& .MuiChip-root": {
        maxWidth: "300px",
        borderRadius: theme.spacing(1),
        "& .MuiChip-label": {
          paddingTop: "4px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
      },
    },
    status: {
      fontSize: "8px",
    },
    completed: {
      "& .MuiChip-icon": {
        color: theme.palette.primary.main,
      },
    },
    failed: {
      "& .MuiChip-icon": {
        color: theme.palette.error.main,
      },
    },
    running: {
      "& .MuiChip-icon": {
        color: "green",
      },
    },
  })
);

export default BatchLogFiles;
